import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import { CharacterVariant, CharacterGenerationOptions } from '@storyverse/shared'
import { Image as ImageIcon, Loader2, RefreshCw, Sparkles, Trash2, Upload, UserCircle } from 'lucide-react'
import React, { useEffect } from 'react'
import { useRemoteConfigStore } from '@/features/shared/stores/config.store'

interface VariantVisualsTabProps {
    variant: CharacterVariant
    handleGenerateImage: () => Promise<void>
    handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleReferencePhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleRemoveReferencePhoto: () => void
    handleRemoveImage: (type: 'ai' | 'user') => void
    handleSetActiveImage: (type: 'ai' | 'user') => void
    generationOptions: CharacterGenerationOptions
    setGenerationOptions: React.Dispatch<React.SetStateAction<CharacterGenerationOptions>>
    referencePhotoPreview: string | null
    imageMode: 'upload' | 'ai'
    setImageMode: React.Dispatch<React.SetStateAction<'upload' | 'ai'>>
    isGenerating: boolean
    fileInputRef: React.RefObject<HTMLInputElement | null>
    referencePhotoInputRef: React.RefObject<HTMLInputElement | null>
    uploadPreview: Record<string, string>
    isReadyToGenerate: boolean
    missingRequirements: {
        missingName: boolean;
        missingGender: boolean;
        missingAge: boolean;
        missingTraits: boolean;
        traitCount: number;
    }
}

export const VariantVisualsTab: React.FC<VariantVisualsTabProps> = ({
    variant,
    handleGenerateImage,
    handleFileUpload,
    handleReferencePhotoUpload,
    handleRemoveReferencePhoto,
    handleRemoveImage,
    handleSetActiveImage,
    generationOptions,
    setGenerationOptions,
    referencePhotoPreview,
    imageMode,
    setImageMode,
    isGenerating,
    fileInputRef,
    referencePhotoInputRef,
    uploadPreview,
    isReadyToGenerate,
    missingRequirements
}) => {
    const manualImageSrc =
        typeof variant.user_image === 'string' && variant.user_image.trim().length > 0
            ? variant.user_image
            : uploadPreview[variant.id]

    const enableCharacterAIImageGeneration = useRemoteConfigStore((state) => state.enableCharacterAIImageGeneration)

    useEffect(() => {
        if (!enableCharacterAIImageGeneration && imageMode === 'ai') {
            setImageMode('upload')
        }
    }, [enableCharacterAIImageGeneration, imageMode, setImageMode])

    return (
        <div className="space-y-6 pt-4 animate-in fade-in duration-300">
            <input
                type="file"
                className="hidden"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileUpload}
            />
            <div className="flex flex-col space-y-8">
                {/* Mode Switcher */}
                <div className="flex justify-center">
                    <div className="flex p-1 bg-muted/30 rounded-full border border-border/50">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setImageMode('upload')}
                            className={cn(
                                "rounded-full px-6 h-8 text-[10px] font-bold uppercase tracking-widest transition-all",
                                imageMode === 'upload' ? "bg-background shadow-sm text-primary" : "text-muted-foreground hover:text-foreground",
                                !enableCharacterAIImageGeneration && "bg-background shadow-sm text-primary max-w-[200px]"
                            )}
                        >
                            <Upload className="w-3.5 h-3.5 mr-2" /> Upload Photo
                        </Button>
                        {enableCharacterAIImageGeneration && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setImageMode('ai')}
                                className={cn(
                                    "rounded-full px-6 h-8 text-[10px] font-bold uppercase tracking-widest transition-all",
                                    imageMode === 'ai' ? "bg-background shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                <Sparkles className="w-3.5 h-3.5 mr-2" /> Character Visual Studio
                            </Button>
                        )}
                    </div>
                </div>

                <div className="max-w-2xl mx-auto w-full">
                    {imageMode === 'upload' ? (
                        /* Manual Upload Section */
                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="relative group w-full aspect-[3/4] max-w-[400px] mx-auto rounded-3xl overflow-hidden shadow-2xl border-2 border-dashed border-border/50 bg-muted/5 flex flex-col items-center justify-center p-4">
                                {variant.user_image ? (
                                    <>
                                        <img
                                            src={manualImageSrc}
                                            alt="Manual Upload"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleRemoveImage('user')}
                                                className="h-8 w-8 rounded-full shadow-xl"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                        <div className="absolute inset-x-0 bottom-4 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => fileInputRef.current?.click()}
                                                className="rounded-full gap-2 shadow-xl backdrop-blur-md bg-background/80"
                                            >
                                                <Upload className="h-4 w-4" /> Change Image
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-center space-y-4">
                                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                                            <ImageIcon className="w-8 h-8" />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-sm font-medium">No image uploaded</p>
                                            <p className="text-xs text-muted-foreground">Drag and drop or click to upload</p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="rounded-full gap-2"
                                        >
                                            <Upload className="h-4 w-4" /> Upload Photo
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {variant.user_image && (
                                <div className="max-w-[400px] mx-auto mt-6 flex items-center justify-between p-4 bg-muted/30 rounded-2xl border border-border/50 animate-in fade-in slide-in-from-top-2 duration-500">
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor="active-image-user" className="text-sm font-bold uppercase tracking-wider cursor-pointer">
                                            Primary Visual
                                        </Label>
                                        <p className="text-[10px] text-muted-foreground">Set this photo as the character's main representation</p>
                                    </div>
                                    <Checkbox
                                        id="active-image-user"
                                        checked={variant.active_image_type === 'user'}
                                        onCheckedChange={(checked) => checked && handleSetActiveImage('user')}
                                        className="h-5 w-5 rounded-md"
                                    />
                                </div>
                            )}
                        </div>
                    ) : enableCharacterAIImageGeneration ? (
                        /* AI Generation Section (Character Visual Studio) */
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="flex flex-col lg:flex-row gap-8 items-start">
                                {/* Left Side: Controls */}
                                <div className="flex-1 flex flex-col space-y-6">
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                                                Additional Instructions
                                            </label>
                                            <Textarea
                                                placeholder="Add specific details (e.g., 'wearing a hat', 'moody lighting')..."
                                                value={generationOptions.userInstruction}
                                                onChange={e => setGenerationOptions((prev: CharacterGenerationOptions) => ({ ...prev, userInstruction: e.target.value }))}
                                                className="min-h-[120px] text-xs resize-none bg-muted/30 border-muted-foreground/20 focus:border-primary/50 transition-colors"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 flex items-center justify-between">
                                                Artist Photo (Optional)
                                                {referencePhotoPreview && (
                                                    <button
                                                        onClick={handleRemoveReferencePhoto}
                                                        className="text-destructive hover:text-destructive/80"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </label>
                                            <input
                                                type="file"
                                                className="hidden"
                                                ref={referencePhotoInputRef}
                                                accept="image/*"
                                                onChange={handleReferencePhotoUpload}
                                            />
                                            <div
                                                onClick={() => !referencePhotoPreview && referencePhotoInputRef.current?.click()}
                                                className={cn(
                                                    "relative group w-full aspect-square rounded-xl overflow-hidden border-2 border-dashed transition-all cursor-pointer flex items-center justify-center bg-muted/30",
                                                    referencePhotoPreview ? "border-primary/30" : "border-muted-foreground/20 hover:border-primary/50"
                                                )}
                                            >
                                                {referencePhotoPreview ? (
                                                    <img src={referencePhotoPreview} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="flex flex-col items-center gap-3 text-center text-muted-foreground/60 p-4">
                                                        <UserCircle className="w-8 h-8" />
                                                        <span className="text-[10px] font-bold uppercase tracking-wider">Upload Artist Reference</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Output */}
                                <div className="flex-1 min-h-[400px] relative">
                                    {variant.look_test_image ? (
                                        <>
                                            <div className="relative group w-full aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl border-2 border-border/50 bg-muted/5 animate-in zoom-in-95 duration-500">
                                                <img
                                                    src={variant.look_test_image as any as string}
                                                    alt="AI Look Test"
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                />
                                                <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={() => handleRemoveImage('ai')}
                                                        className="h-8 w-8 rounded-full shadow-xl p-0"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                    <Button
                                                        size="lg"
                                                        onClick={handleGenerateImage}
                                                        disabled={isGenerating || !isReadyToGenerate}
                                                        className="w-full rounded-2xl shadow-2xl font-bold gap-3 py-6 glass-button border-white/20"
                                                    >
                                                        {isGenerating ? (
                                                            <Loader2 className="h-5 w-5 animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="h-4 w-4" />
                                                        )}
                                                        {isReadyToGenerate ? 'Regenerate Image' : 'Name Required'}
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="mt-6 flex items-center justify-between p-4 bg-muted/30 rounded-2xl border border-border/50 animate-in fade-in slide-in-from-top-2 duration-500">
                                                <div className="flex flex-col gap-1">
                                                    <Label htmlFor="active-image-ai" className="text-sm font-bold uppercase tracking-wider cursor-pointer">
                                                        Set as Primary Look
                                                    </Label>
                                                    <p className="text-[10px] text-muted-foreground">Use this AI generation as the character's active portrait</p>
                                                </div>
                                                <Checkbox
                                                    id="active-image-ai"
                                                    checked={variant.active_image_type === 'ai'}
                                                    onCheckedChange={(checked) => checked && handleSetActiveImage('ai')}
                                                    className="h-5 w-5 rounded-md"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <div className="relative flex flex-col items-center justify-center text-center h-full min-h-[400px] rounded-3xl border-2 border-dashed border-border/20 bg-muted/5 p-8 space-y-8">
                                            <div className="space-y-6 flex flex-col items-center">
                                                <div className="w-20 h-20 rounded-full bg-primary/5 flex items-center justify-center text-primary/30 border border-primary/10">
                                                    <ImageIcon className="w-10 h-10" />
                                                </div>
                                                <div className="space-y-2">
                                                    <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                                                        {referencePhotoPreview ? "Hybrid Generation Ready" : "AI Look Test Chamber"}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground/60 max-w-[280px] leading-relaxed">
                                                        {referencePhotoPreview
                                                            ? "Using both the artist reference and character details to craft a perfect cinematic portrait."
                                                            : "Your character's physical traits and instructions will be used to generate a unique visual identity."}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-center gap-4 w-full max-w-[320px]">
                                                <Button
                                                    size="lg"
                                                    onClick={handleGenerateImage}
                                                    disabled={isGenerating || !isReadyToGenerate}
                                                    className="w-full rounded-2xl shadow-xl font-bold gap-3 py-6"
                                                >
                                                    {isGenerating ? (
                                                        <Loader2 className="h-5 w-5 animate-spin" />
                                                    ) : (
                                                        <Sparkles className="h-5 w-5" />
                                                    )}
                                                    Generate Character
                                                </Button>
                                                {!isReadyToGenerate && (
                                                    <div className="flex flex-col items-center gap-1 animate-in fade-in slide-in-from-top-1 text-center">
                                                        <p className="text-[10px] text-destructive font-bold uppercase tracking-wider">
                                                            Description Required
                                                        </p>
                                                        <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 max-w-[280px]">
                                                            {missingRequirements.missingName && <span className="text-[9px] text-muted-foreground">• Full Name</span>}
                                                            {missingRequirements.missingGender && <span className="text-[9px] text-muted-foreground">• Gender</span>}
                                                            {missingRequirements.missingAge && <span className="text-[9px] text-muted-foreground">• Age</span>}
                                                            {missingRequirements.missingTraits && (
                                                                <span className="text-[9px] text-muted-foreground">
                                                                    • {missingRequirements.traitCount === 0 ? '2 physical traits' : '1 more physical trait'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {isGenerating && (
                                        <div className="absolute inset-0 z-20 p-px flex justify-center bg-background/50 backdrop-blur-sm rounded-3xl animate-in fade-in duration-300">
                                            <div className="flex flex-col items-center justify-center space-y-4">
                                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Sculpting Visuals...</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div >
    )
}
